import { useState, useEffect, useCallback } from 'react';
import Spinner from '../components/common/Spinner';
import Badge from '../components/common/Badge';
import EmptyState from '../components/common/EmptyState';
import { api } from '../services/apiClient';

export default function DriveJobsPage({ toast }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try { setJobs(await api.driveJobs()); } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  async function handleDeleteJob(id) {
    if (!window.confirm('Delete this job? This only removes the job log, not any invoices it created.')) return;
    setDeletingId(id);
    try {
      await api.deleteJob(id);
      setJobs(prev => prev.filter(j => j._id !== id));
      toast?.('Job deleted', 'success');
    } catch (e) {
      toast?.('Failed to delete job: ' + e.message, 'error');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleDeleteAllJobs() {
    if (!jobs.length) return;
    if (!window.confirm(`Delete all ${jobs.length} job(s)? This only removes the job logs, not any invoices they created.`)) return;
    try {
      const result = await api.deleteAllJobs();
      setJobs([]);
      toast?.(`Deleted ${result.deleted} job(s)`, 'success');
    } catch (e) {
      toast?.('Failed to delete all jobs: ' + e.message, 'error');
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spinner dark /></div>;

  if (!jobs.length) {
    return (
      <EmptyState
        icon="📁"
        title="No Drive scan jobs yet"
        sub='Use "Scan Drive" to start a job.'
      />
    );
  }

  return (
    <div>
      <div className="list-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="list-title">Drive Scan Jobs</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={load}>↻ Refresh</button>
          <button className="btn btn-outline btn-sm" onClick={handleDeleteAllJobs} style={{ color: 'var(--danger, #d9534f)' }}>
            🗑 Delete All
          </button>
        </div>
      </div>
      {jobs.map(j => (
        <div className="job-card" key={j._id}>
          <div className="job-header">
            <div>
              <div className="job-title">{j.type === 'sale' ? '📦' : '🧾'} {j.type} scan</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                Root: {j.rootFolderId} · Folders: {j.foldersProcessed || 0}/{j.foldersFound || 0} · Files: {j.filesProcessed || 0}/{j.filesFound || 0}
                {j.invoiceIds?.length > 0 && <> · Invoices: {j.invoiceIds.length}</>}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Badge variant={j.status === 'done' ? 'success' : j.status === 'error' ? 'danger' : j.status === 'processing' ? 'warning' : 'gray'}>
                {j.status === 'processing' && <Spinner />} {j.status}
              </Badge>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => handleDeleteJob(j._id)}
                disabled={deletingId === j._id}
                title="Delete this job"
              >
                {deletingId === j._id ? '…' : '🗑'}
              </button>
            </div>
          </div>
          <div className="job-log">
            {j.log?.slice(-15).map((l, i) => (
              <div key={i} className={`job-log-entry ${l.msg?.startsWith('✓') || l.msg?.startsWith('✅') ? 'ok' : l.msg?.startsWith('✗') || l.msg?.startsWith('ERROR') ? 'err' : ''}`}>
                {new Date(l.ts).toLocaleTimeString()} — {l.msg}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
